/**
 * `/opening` (v1 ticket 40): the first night's cold open, then the eavesdropper's map in `first` mode over its held
 * picture, then the example (`<eaves-example>`, a café overheard and let go, nothing kept), then the learner's land on
 * home. A replay lays the example over itself (`opening.ts`) and goes home after it.
 *
 * This element is small and in the main bundle; the cinematic, the map and their art are the `opening` chunk
 * (`web/src/opening/index.ts`), loaded here only. The opening, the map and the example are fixed layers on
 * `document.body`, so they outlive this route: the example's end holds it and the opening (`holdFirstNight`)
 * and goes home, and home takes them away after its land's first frame (each fades and is removed at its own
 * animation's end). Until then the old picture stays fully on screen.
 *
 * `POST /api/town/seen {part: 'opening'}` goes when the opening ends, done or skipped (the map comes up either way).
 * Opened while the opening is not due (a bookmark, a second device), it plays as a replay, with no naming after it,
 * then the example, and goes home.
 */
import { LitElement, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { api } from '../api';
import { tzOf } from '../drafts-ui';
import { ackTownSeen, holdFirstNight, markShown } from '../first-night';
import { currentLang, currentTarget } from '../i18n';
import { go } from '../router';
import { normaliseTownName } from '../town-name.generated';
import type { CafeSound, EavesExample, EavesMap, EavesOpening, MapDoneDetail } from '../opening';
import type { Allowance } from '../types';
import type { OwnTown } from '../town/own-town';
import { beginFirstRunJourney, firstRunMilestone } from '../research-client';

/** The opening chunk: the cinematic, the map and the first landing's map (never in the main bundle). */
export const loadOpening = () => import('../opening');

@customElement('opening-view')
export class OpeningView extends LitElement {
  private opening: EavesOpening | null = null;
  private map: EavesMap | null = null;
  private example: EavesExample | null = null;
  /** The loaded chunk (the café's sound is made from it inside the tap that starts the example). */
  private chunk: Awaited<ReturnType<typeof loadOpening>> | null = null;
  /** The layers went to home held: they stay when this route goes. */
  private handed = false;
  private run = 0;

  protected createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    void this.start(++this.run);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.run++;
    if (this.handed) return;
    this.opening?.remove();
    this.map?.remove();
    this.example?.remove();
    this.opening = this.map = this.example = null;
  }

  private async start(run: number) {
    let own: OwnTown | null = null, allowance: Allowance | undefined;
    try {
      [this.chunk, own, allowance] = await Promise.all([
        loadOpening(),
        api.town().catch(() => null),
        api.allowance().catch(() => undefined),
      ]);
    } catch (e) {
      // The chunk did not load (offline, a new build): the land waits on home, and the opening is not asked again
      // in this visit.
      console.warn('the opening did not load', (e as Error).name);
      if (run === this.run) { markShown('opening'); go('/', true); }
      return;
    }
    if (run !== this.run) return;
    const first = !!own?.due?.opening;
    if (first) void beginFirstRunJourney();
    markShown('opening');
    const op = document.createElement('eaves-opening') as EavesOpening;
    op.target = own?.language ?? currentTarget();
    op.explain = currentLang();
    op.replay = !first;
    op.townName = own?.name ?? null;
    op.foundedAt = own?.foundedAt ?? null;
    op.addEventListener('opening-end', (event) => {
      const task = (event as CustomEvent<{ task?: 'record' | 'wanted' }>).detail?.task;
      this.ended(run, op, allowance, own, task);
    }, { once: true });
    this.opening = op;
    document.body.append(op);
  }

  /** The opening ended, done or skipped: the map comes up over its held picture (a replay just goes home). */
  private ended(run: number, op: EavesOpening, allowance: Allowance | undefined, own: OwnTown | null, task?: 'record' | 'wanted') {
    if (run !== this.run) return;
    if (op.replay) {
      // A replay has played its example and faded itself out: it goes, and home comes back.
      op.remove();
      this.opening = null;
      go(task ? task === 'record' ? '/#record' : '/#type' : '/', true);
      return;
    }
    const seen = ackTownSeen('opening');
    const map = document.createElement('eaves-map') as EavesMap;
    map.target = op.target;
    map.explain = op.explain;
    map.mode = 'first';
    map.check = normaliseTownName;
    map.lay = (name) => api.townName(name);
    map.timeZone = tzOf(allowance);
    map.quiet = op.muted;
    map.suggested = own?.suggestedName ?? '';
    map.addEventListener('example-offered', () => firstRunMilestone('example.offered'), { once: true });
    // The night ambience plays on, softer, under the map.
    op.hushTo(0.6);
    // First mode fires only at Continue, with the stone laid.
    map.addEventListener('map-done', (e) => {
      const d = (e as CustomEvent<MapDoneDetail>).detail;
      // A pair with no café script has no example: straight to the land, as before the example.
      if (!d?.example) {
        if (this.chunk?.openingFor(op.target, op.explain)) firstRunMilestone('example.skipped');
        void this.mapToLand(run, op, map, seen);
        return;
      }
      if (!this.chunk?.openingFor(op.target, op.explain)) { void this.mapToLand(run, op, map, seen); return; }
      this.showExample(run, op, this.chunk?.CafeSound.create() ?? null, seen, d?.name ?? own?.name ?? null, d?.foundedAt ?? own?.foundedAt ?? null, allowance);
    }, { once: true });
    this.map = map;
    document.body.append(map);
  }

  /**
   * The example (after the stone): the learner's own land as home shows it, with a café to try once, nothing kept.
   * `sound` was made inside the tap that got here. It is drawn under the map, which lifts away once the land is framed.
   */
  private async showExample(run: number, op: EavesOpening, sound: CafeSound | null, seen: Promise<void>,
    name: string | null, foundedAt: string | null, allowance: Allowance | undefined) {
    if (run !== this.run) { sound?.dispose(); return; }
    const ex = document.createElement('eaves-example') as EavesExample;
    ex.target = op.target;
    ex.explain = op.explain;
    ex.host = op;
    ex.sound = sound;
    ex.name = name;
    ex.foundedAt = foundedAt;
    ex.allowance = allowance;
    ex.classList.add('ex-under');
    ex.addEventListener('example-end', (event) => {
      const task = (event as CustomEvent<{ task?: 'record' | 'wanted' }>).detail?.task;
      void this.toLand(run, op, ex, seen, task);
    }, { once: true });
    this.example = ex;
    document.body.append(ex);
    await Promise.race([ex.ready, new Promise((r) => setTimeout(r, 4000))]);
    if (run !== this.run) return;
    const map = this.map;
    if (!map) { ex.classList.remove('ex-under'); return; }
    this.map = null;
    const done = () => { map.remove(); ex.classList.remove('ex-under'); };
    void map.lift().then(done, done);
  }

  /** No example to try: the opening and the map stay, held, while home draws the land under them. */
  private async mapToLand(run: number, op: EavesOpening, map: EavesMap, seen: Promise<void>) {
    if (run !== this.run) return;
    this.handed = true;
    this.map = null;
    holdFirstNight(
      { go: () => map.lift(), remove: () => map.remove() },
      { go: () => op.leave(), remove: () => op.remove() },
    );
    await seen;
    go('/', true);
  }

  /** Start: the opening and the example stay, held, while home draws the land under them. */
  private async toLand(run: number, op: EavesOpening, ex: EavesExample, seen: Promise<void>, task?: 'record' | 'wanted') {
    if (run !== this.run) return;
    this.handed = true;
    holdFirstNight(
      { go: () => ex.leave(), remove: () => ex.remove() },
      { go: () => op.leave(), remove: () => op.remove() },
    );
    await seen;
    go(task ? task === 'record' ? '/#record' : '/#type' : '/', true);
  }

  render() {
    // Under the opening while its chunk loads: the same night, so nothing flashes.
    return html`<div class="opening-cover" aria-hidden="true" style="position:fixed;inset:0;background:#05060f;z-index:60"></div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'opening-view': OpeningView;
  }
}
